/* eslint-disable max-lines */
import { Category } from '@wix/answers-api/dist/kb';
import { clickOnAnswer } from '@wix/bi-logger-new-help-center/v2';
import {
  Box,
  Divider,
  Heading,
  Loader,
  Pagination,
  Text,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import { FunctionComponent, useContext } from 'react';
import { animateScroll } from 'react-scroll';
import {
  BI,
  EXPERIMENTS,
  LOCALES,
  GEN_ANSWER_RELATED_ARTICLES_COUNT,
} from '../../constants';
import { DATA_HOOKS } from '../../dataHooks';
import { useBI } from '../../hooks/useBI';
import { SEARCH_RESULTS_PAGE_SIZE } from '../../pages/search';
import {
  ArticleSearchResult,
  ArticleSearchResultDocumentType,
  ArticleSearchResultItem,
} from '../../types';
import { mapArticleTypeToItemType } from '../../utils/bi';
import { Breadcrumbs } from '../Breadcrumbs';
import { RenderHTML } from '../RenderHTML';
import { SearchResultItem } from './SearchResultItem';
import { SearchResultItem as SearchResultItemV2 } from './SearchResultItemV2';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import { isMobile, isTablet } from '../../utils/userAgent';
import { Context } from '../../context';
import { GeneratedAnswer } from './GeneratedAnswer';
import {
  ItemOnPageEventSource,
  useBiOnVisibleItem,
} from '../../hooks/useBiOnVisibleItem';
import classNames from 'classnames';
import { SkeletonLoading } from './SkeletonLoading';
import css from './index.module.scss';

export type SearchResultsProps = {
  term: string;
  searchResults: ArticleSearchResult;
  categoriesPaths: Record<string, Category[]>;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  generatedAnswer: string | null;
  relatedArticlesTitles?: string[];
  isProhibited: boolean | null;
  retryGenerateAnswer: () => Promise<void>;
};

export const SearchResults: FunctionComponent<SearchResultsProps> = ({
  term,
  searchResults,
  categoriesPaths,
  currentPage,
  onPageChange,
  isLoading,
  generatedAnswer,
  relatedArticlesTitles,
  isProhibited,
  retryGenerateAnswer,
}) => {
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();
  const { itemsCount } = searchResults;
  const { experiments } = useExperiments({ readOnly: true });
  const context = useContext(Context);
  const { searchSessionId } = context;
  const isMobileView = isMobile(context);
  const isTabletView = isTablet(context);
  const { itemRef } = useBiOnVisibleItem(
    ItemOnPageEventSource.MORE_SEARCH_RESULTS
  );

  const isNextGenSearchEnabled =
    experiments.enabled(EXPERIMENTS.SPECS.NEXT_GEN_SEARCH) &&
    locale === LOCALES.EN &&
    !isMobileView &&
    !isTabletView;

  const isNewSearchResultImprovements = experiments.enabled(
    EXPERIMENTS.SPECS.SEARCH_RESULT_IMPROVEMENTS
  );

  const displayGeneratedAnswer = isNextGenSearchEnabled && currentPage === 1;

  const displayGeneratedAnswerLoading =
    isNextGenSearchEnabled && generatedAnswer === null && currentPage === 1;

  const items: ArticleSearchResultItem[] = searchResults.items;

  const onPageChangeEvent = async (page: number) => {
    animateScroll.scrollToTop();
    onPageChange(page);
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.PAGE_SELECTION,
        clicked_url: `${window.location.origin}/search&page=${page}`,
        clicked_text: `${page}`,
      })
    );
  };

  const onArticleTopicBadgeClick = async (
    topic: Category,
    itemOrder: number
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.CATEGORY_SELECTION,
        clicked_url: topic.url,
        clicked_text: topic.name,
        clicked_item_order: `${itemOrder}`,
        clicked_item_id: topic.id,
      })
    );
  };

  const onArticleResolutionBadgeClick = async (
    clickedText: string,
    url: string,
    itemOrder: number
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.TAG_SELECTION,
        clicked_text: clickedText,
        clicked_url: url,
        clicked_item_order: `${itemOrder}`,
      })
    );
  };

  const onArticleTypeTagClick = async (
    clickedText: string,
    url: string,
    itemOrder: number
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.SOURCE_HOMEPAGE_SELECTION,
        clicked_text: clickedText,
        clicked_url: url,
        clicked_item_order: `${itemOrder}`,
      })
    );
  };

  const onArticleClick = async (
    article: ArticleSearchResultItem,
    itemOrder: number
  ) => {
    await sendBIEvent(
      clickOnAnswer({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_url: article.url,
        clicked_item_id: article.id,
        clicked_item_order: `${itemOrder}`,
        question: term,
        item_type:
          article.docType === ArticleSearchResultDocumentType.WIX_DOCS_ARTICLE
            ? BI.CLICKED_ITEM_TYPES.WIX_DOCS
            : mapArticleTypeToItemType(article.type),
        clicked_item_type:
          article.docType === ArticleSearchResultDocumentType.WIX_DOCS_ARTICLE
            ? BI.CLICKED_ITEM_TYPES.WIX_DOCS
            : mapArticleTypeToItemType(article.type),
        search_result_selection:
          BI.SEARCH_RESULT_SELECTION.FULL_SEARCH_RESULTS_LIST,
        page_number: currentPage,
        search_method: searchResults.searchMethod,
        ...(article?.distance
          ? { output_distance_str: `${article.distance}` }
          : {}),
        search_session_Id: searchSessionId,
      })
    );
  };

  const numPages = Math.ceil(itemsCount / SEARCH_RESULTS_PAGE_SIZE);

  const breadcrumbItems = [
    { id: 'search-results', label: t('search-results-page.breadcrumb') },
  ];

  const resultsTitle =
    itemsCount >= 0
      ? t('search-results-page.title', { count: itemsCount, term })
      : t('search-results-page.title-single', { term });

  const genAnswerResultsTitleGrey = t(
    'search-results-page.title-answer-gen-more'
  );

  const genAnswerResultsTitleBlack = ` ${t(
    'search-results-page.title-answer-gen-term',
    { term }
  )}`;

  const topArticles = searchResults.items.slice(
    0,
    GEN_ANSWER_RELATED_ARTICLES_COUNT
  );
  const relatedArticles = topArticles.filter((article) =>
    relatedArticlesTitles?.includes(article.title)
  );

  return (
    <Box
      direction="vertical"
      className={css.searchResultsWrapper}
      dataHook={DATA_HOOKS.SEARCH_RESULTS}
    >
      <Breadcrumbs breadcrumbItems={breadcrumbItems} shallow={false} />
      {displayGeneratedAnswer && (
        <GeneratedAnswer
          searchQuestion={term}
          searchAnswer={generatedAnswer}
          articleLinks={relatedArticles}
          isProhibited={isProhibited}
          retryGenerateAnswer={retryGenerateAnswer}
        />
      )}
      <Box
        direction="vertical"
        className={classNames(css.searchResults, {
          [css.includeGeneratedAnswer]: displayGeneratedAnswer,
          [css.searchResultsIncludeV2]: isNewSearchResultImprovements,
        })}
      >
        <Box
          className={classNames(css.searchResultsHeader, {
            [css.nextGenSearchDisabled]: !isNextGenSearchEnabled,
            [css.isNewSearchResultImprovements]: isNewSearchResultImprovements,
          })}
          verticalAlign="middle"
          align="space-between"
        >
          {!displayGeneratedAnswerLoading && (
            <Heading
              appearance={
                isNewSearchResultImprovements && !displayGeneratedAnswer
                  ? 'H4'
                  : 'H3'
              }
              ellipsis
              dataHook={DATA_HOOKS.SEARCH_RESULTS_TITLE}
              className={css.searchResultsTitle}
            >
              {!displayGeneratedAnswer ? (
                <RenderHTML
                  html={resultsTitle}
                  className={css.searchResultsTitleHTML}
                  textElement={
                    isMobileView
                      ? {
                          ellipsis: true,
                          maxLines: 3,
                        }
                      : undefined
                  }
                />
              ) : (
                <Box
                  dataHook={DATA_HOOKS.MORE_RESULTS}
                  className={classNames(css.searchResultsTitleHTML, {
                    [css.generatedAnswerWide]: displayGeneratedAnswer,
                  })}
                  ref={itemRef}
                  direction="horizontal"
                >
                  <RenderHTML
                    className={css.searchResultsTitleGrey}
                    html={genAnswerResultsTitleGrey}
                    headingElement={{ size: 'large' }}
                  />
                  <RenderHTML
                    html={genAnswerResultsTitleBlack}
                    headingElement={{ ellipsis: true, size: 'large' }}
                  />
                </Box>
              )}
            </Heading>
          )}
        </Box>
        {isLoading && locale === LOCALES.EN ? (
          displayGeneratedAnswerLoading ? (
            <SkeletonLoading />
          ) : (
            <Box align="center" className={css.loaderWrapper}>
              <Loader />
            </Box>
          )
        ) : displayGeneratedAnswerLoading ? (
          <></>
        ) : (
          <Box direction="vertical" className={css.searchResultsList}>
            {items.length > 0 ? (
              items.map((searchResult, idx) => (
                <>
                  {experiments.enabled(
                    EXPERIMENTS.SPECS.SHOW_DISTANCE_FOR_RAM
                  ) ? (
                    <Text weight="bold">Distance: {searchResult.distance}</Text>
                  ) : null}
                  {isNewSearchResultImprovements ? (
                    <SearchResultItemV2
                      key={searchResult.id}
                      rootTopic={
                        categoriesPaths?.[searchResult.categoryId]?.[0]
                      }
                      onArticleResolutionBadgeClick={(
                        clickedText: string,
                        url: string
                      ) =>
                        onArticleResolutionBadgeClick(clickedText, url, idx + 1)
                      }
                      onArticleTypeTagClick={(
                        clickedText: string,
                        url: string
                      ) => onArticleTypeTagClick(clickedText, url, idx + 1)}
                      onArticleTopicBadgeClick={(topic) =>
                        onArticleTopicBadgeClick(topic, idx + 1)
                      }
                      onArticleClick={() =>
                        onArticleClick(searchResult, idx + 1)
                      }
                      {...searchResult}
                      isGeneratedAnswerDisplayed={displayGeneratedAnswer}
                      docType={searchResult.docType}
                    />
                  ) : (
                    <SearchResultItem
                      key={searchResult.id}
                      rootTopic={
                        categoriesPaths?.[searchResult.categoryId]?.[0]
                      }
                      onArticleResolutionBadgeClick={(
                        clickedText: string,
                        url: string
                      ) =>
                        onArticleResolutionBadgeClick(clickedText, url, idx + 1)
                      }
                      onArticleTypeTagClick={(
                        clickedText: string,
                        url: string
                      ) => onArticleTypeTagClick(clickedText, url, idx + 1)}
                      onArticleTopicBadgeClick={(topic) =>
                        onArticleTopicBadgeClick(topic, idx + 1)
                      }
                      onArticleClick={() =>
                        onArticleClick(searchResult, idx + 1)
                      }
                      {...searchResult}
                      isGeneratedAnswerDisplayed={displayGeneratedAnswer}
                      docType={searchResult.docType}
                    />
                  )}
                  {idx < items.length - 1 ? <Divider /> : null}
                </>
              ))
            ) : (
              <Box
                direction="vertical"
                align="center"
                className={css.noResultsForFilter}
                dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOR_FILTER}
              >
                <Heading
                  appearance="H3"
                  className={css.noResultsForFilterTitle}
                >
                  {t(
                    'search-results-page.no-results-found-under-filter.title',
                    {
                      term,
                    }
                  )}
                </Heading>
                <Text
                  size="medium"
                  className={css.noResultsForFilterDescription}
                >
                  {t(
                    'search-results-page.no-results-found-under-filter.description'
                  )}
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Box>
      {!(isLoading || displayGeneratedAnswerLoading) &&
      itemsCount > SEARCH_RESULTS_PAGE_SIZE ? (
        <Pagination
          className={css.pagination}
          onChange={(e) => onPageChangeEvent(e.page)}
          currentPage={currentPage}
          totalPages={numPages}
          dataHook={DATA_HOOKS.SEARCH_RESULTS_PAGINATION}
        />
      ) : null}
    </Box>
  );
};
